"use client";
//test
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contaxt/AuthContext";
import { getExamDetailsApi } from "../api/examApi";
import { ChevronDown, ChevronUp, FileText } from "lucide-react";
import { Card, CardHeader } from "@/components/ui/card";
import "../styles/DataTable.css";

export interface TaskAttempt {
  id: number;
  exam_id: string;
  task_id: number;
  user_id: string;
  grammar: number | null;
  strength: string | null;
  created_at: string;
  grammar_tips: string | null;
}

export interface Topic {
  id: number;
  content: string;
  created_at: string;
}

export interface Task {
  id: number;
  exam_id: string;
  user_id: string;
  topic: Topic | null;
  estimated_cefr: string | null;
  task_type: number;
  created_at: string;
  task_attempts: TaskAttempt[];
  attempts_count: number;
}

export interface TaskRowData {
  taskNumber: string;
  taskId: number;
  taskType: number;
  topic: Topic | null;
  cefrLevel: string;
  totalAttempts: number;
  createdAt: string;
  task_attempts: TaskAttempt[];
}

const staticTopics: Record<number, string> = {
  1: "Talk about yourself",
  2: "Information desk roleplay",
  3: "Opinion and argumentation topic",
};

export default function ExamHistoryPage() {
  const { getPaidUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const query = new URLSearchParams(location.search);
  const examId = query.get("examId");
  const userId = query.get("userId");

  const [taskData, setTaskData] = useState<TaskRowData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const fetchExamDetails = async () => {
    try {
      setLoading(true);
      const paidUser = getPaidUser();

      if (!examId || !userId || userId !== paidUser.userId) {
        throw new Error("Invalid examId or userId");
      }

      const res = await getExamDetailsApi(paidUser, examId);

      const formatted: TaskRowData[] = Object.entries(res.tasks).map(
        ([taskNumber, tasks]) => {
          const task = (tasks as Task[])[0];

          return {
            taskNumber,
            taskId: task.id,
            taskType: task.task_type,
            topic: task.topic,
            cefrLevel: task.estimated_cefr || "N/A",
            totalAttempts: Math.max(0, task.attempts_count || 0),
            createdAt: task.created_at,
            task_attempts: task.task_attempts || [],
          };
        }
      );

      setTaskData(formatted);
    } catch (error) {
      console.error("Error fetching exam details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExamDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examId, userId]);

  const renderExpandedContent = (row: TaskRowData) => {
    if (row.task_attempts.length === 0) {
      return (
        <div className="expanded-empty">
          No attempts found for this task.
        </div>
      );
    }

    return (
      <div className="expanded-content">
        <h4 className="expanded-title">Task Attempts Details:</h4>
        <table className="expanded-table">
          <thead>
            <tr>
              <th>Attempt #</th>
              <th>Grammar Score</th>
              <th>Strength</th>
              <th>Grammar Tips</th>
              <th>Date &amp; Time</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {row.task_attempts.map((attempt, index) => (
              <tr key={attempt.id}>
                <td>{index + 1}</td>
                <td>
                  {attempt.grammar !== null ? (
                    <span
                      className={`grammar-score-badge ${
                        attempt.grammar >= 70
                          ? "grammar-score-excellent"
                          : attempt.grammar >= 60
                          ? "grammar-score-good"
                          : "grammar-score-needs-improvement"
                      }`}
                    >
                      {attempt.grammar}
                    </span>
                  ) : (
                    <span className="text-na">N/A</span>
                  )}
                </td>
                <td>
                  {attempt.strength ? (
                    attempt.strength
                  ) : (
                    <span className="text-na">N/A</span>
                  )}
                </td>
                <td>
                  {attempt.grammar_tips ? (
                    attempt.grammar_tips
                  ) : (
                    <span className="text-na">N/A</span>
                  )}
                </td>
                <td>{new Date(attempt.created_at).toLocaleString()}</td>
                <td>
                  <button
                    className="expanded-view-btn"
                    onClick={() =>
                      navigate("/single-task-feedback", {
                        state: {
                          userId: attempt.user_id,
                          examId: attempt.exam_id,
                          taskAttemptId: attempt.id,
                          taskType: row.taskType,
                        },
                      })
                    }
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="exam-history-loading">
        <div className="exam-history-spinner" />
        <p className="exam-history-loading-text">Loading exam history...</p>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center px-4">
      {/* Header */}
      <div className="w-full flex flex-col items-center">
        <div className="page-header">
          <h1 className="page-title">Exam details</h1>
          <p className="page-subtitle">Track your performance across all tasks</p>
        </div>

        {/* Main Card / Table */}
        <Card className="exam-history-card">
          <div className="exam-history-scroll">
            <table className="exam-history-table">
              <thead className="exam-history-table-header">
                <tr>
                  <th>Task Type</th>
                  <th>CEFR Level</th>
                  <th>Topic</th>
                  <th>Total Attempts</th>
                  <th>Created At</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {taskData.length === 0 && (
                  <tr>
                    <td colSpan={6}>
                      <div className="exam-history-empty-state">
                        <FileText className="exam-history-empty-icon" />
                        <h3 className="exam-history-empty-title">
                          No records found
                        </h3>
                        <p className="exam-history-empty-description">
                          Your task attempts will appear here after your first
                          exam.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}

                {taskData.map((row, idx) => (
                  <React.Fragment key={row.taskId}>
                    <tr
                      className={`exam-history-table-row ${
                        expandedRow === idx ? "expanded" : ""
                      }`}
                    >
                      <td>{row.taskType}</td>

                      <td>
                        {row.cefrLevel && row.cefrLevel !== "N/A" ? (
                          <span className="cefr-badge">{row.cefrLevel}</span>
                        ) : (
                          <span className="text-na">N/A</span>
                        )}
                      </td>

                      <td>
                        <span
                          className="exam-history-topic"
                          title={row.topic?.content || staticTopics[row.taskType]}
                        >
                          {row.topic?.content
                            ? row.topic.content
                            : staticTopics[row.taskType] ?? "N/A"}
                        </span>
                      </td>

                      <td>
                        <span className="attempts-badge">
                          {row.totalAttempts} attempts
                        </span>
                      </td>
                      <td>
                        {new Date(row.createdAt).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                        })}
                      </td>

                      <td>
                        {row.totalAttempts > 0 ? (
                          <button
                            onClick={() =>
                              setExpandedRow(expandedRow === idx ? null : idx)
                            }
                            className="expand-toggle-btn"
                          >
                            {expandedRow === idx ? (
                              <ChevronUp size={16} className="chevron-icon" />
                            ) : (
                              <ChevronDown size={16} className="chevron-icon" />
                            )}
                          </button>
                        ) : (
                          <span className="no-attempts-text">No attempts</span>
                        )}
                      </td>
                    </tr>

                    {expandedRow === idx && (
                      <tr className="expanded-row">
                        <td colSpan={6}>{renderExpandedContent(row)}</td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Bottom spacing to match dashboard rhythm */}
        <div className="exam-history-bottom-space" />
      </div>
    </div>
  );
}
